import {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { Tooltip } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import RefreshIcon from '@mui/icons-material/Refresh'
import CloseIcon from '@mui/icons-material/Close'
import ChatIcon from '@mui/icons-material/Chat'
import AttachmentIcon from '../icons/AttachmentIcon'
import { AiAgentMessageList } from '../../features/AiAgentChat/AiAgentMessageList'
import { useAiAgentConversation } from '../../features/AiAgentChat/hooks/useAiAgentConversation'
import { usePageContext } from '../../features/AiAgentChat/hooks/usePageContext'
import { useAiAgentImageUpload } from '../../features/AiAgentChat/hooks/useAiAgentImageUpload'
import { useImageDropZone } from '../../features/AiAgentChat/hooks/useImageDropZone'
import { useFabProximity } from '../../features/AiAgentChat/hooks/useFabProximity'
import { useFabDismissal } from '../../features/AiAgentChat/hooks/useFabDismissal'
import { AllState } from '../../../store'
import {
  clearWbsItemAttachments,
  removeWbsItemAttachment,
  selectOpenRequestSeq,
  selectWbsItemAttachments,
} from '../../../store/aiAgentChat'
import {
  ChatFab,
  FabContainer,
  FabDisplayState,
  DismissButton,
  ChatPanel,
  PanelHeader,
  HeaderTitle,
  HeaderActions,
  InputArea,
  ChatInput,
  SendButton,
  HeaderIconButton,
  InlineError,
  AttachmentsPreviewRow,
  AttachmentPreviewItem,
  AttachmentThumb,
  AttachmentUploadingOverlay,
  RemoveAttachmentButton,
  DropOverlay,
} from './styles'
import { WbsItemAttachmentChip } from '../../features/AiAgentChat/WbsItemAttachmentChip'

export const AiAgentChat = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  // 画面側からpushされたWBSアイテム添付（右クリックメニュー・単票ヘッダー等から追加される）。
  const wbsItemAttachments = useSelector((state: AllState) =>
    selectWbsItemAttachments(state)
  )
  // 画面側から「チャットを開いてほしい」と要求された回数。増えたら1回の要求とみなす。
  const openRequestSeq = useSelector((state: AllState) =>
    selectOpenRequestSeq(state)
  )
  const { messages, isStreaming, inlineError, sendMessage, resetConversation } =
    useAiAgentConversation({
      greetingText: intl.formatMessage({ id: 'aiAgentChat.greeting' }),
      busyErrorText: intl.formatMessage({ id: 'aiAgentChat.error.busy' }),
      generalErrorText: intl.formatMessage({ id: 'aiAgentChat.error.general' }),
      streamErrorText: intl.formatMessage({
        id: 'aiAgentChat.error.streaming',
      }),
    })
  const pageContext = usePageContext()
  const {
    attachments,
    errorMessage: attachErrorMessage,
    isUploading,
    addFiles,
    removeAttachment,
    clearAttachments,
    getUploadedImageRefs,
  } = useAiAgentImageUpload()
  // AI応答中は添付ボタンと同様にドロップも受け付けない。
  const { isDraggingOver, dropZoneProps } = useImageDropZone({
    onDropFiles: addFiles,
    disabled: isStreaming,
  })
  const fileInputRef = useRef<HTMLInputElement>(null)
  // ユーザーが起動ボタンを画面外へ引っ込めたかどうか。リロードをまたいで保持する。
  const { isDismissed, isRestored, changeDismissed } = useFabDismissal()
  const isNearCorner = useFabProximity(isDismissed)

  // 画面側からWBSアイテムを添付されたときは、チャットを開いて添付内容を見せる。
  // 起動ボタンを引っ込めている場合は常設状態に戻す（起動ボタンを押したときと同じ扱い）。
  const handledOpenRequestSeq = useRef(openRequestSeq)
  useEffect(() => {
    if (openRequestSeq === handledOpenRequestSeq.current) return
    // 引っ込め状態の復元を待つ。復元前に切り替えると復元値で上書きされ、再び隠れてしまう。
    if (!isRestored) return
    handledOpenRequestSeq.current = openRequestSeq
    if (isDismissed) {
      changeDismissed(false)
    }
    setOpen(true)
  }, [openRequestSeq, isRestored, isDismissed, changeDismissed])

  const displayState: FabDisplayState = !isDismissed
    ? 'visible'
    : isNearCorner
      ? 'peeking'
      : 'hidden'

  const handleFabClick = () => {
    // 顔を出しているだけの状態からは、常設状態に戻すと同時にチャットを開く。
    if (isDismissed) {
      changeDismissed(false)
      setOpen(true)
      return
    }
    setOpen(prev => !prev)
  }

  const handleDismiss = () => {
    changeDismissed(true)
    setOpen(false)
  }

  const handleSendMessage = () => {
    if (isStreaming || inputValue.trim() === '' || isUploading) {
      return
    }
    const messageText = inputValue
    const imageRefs = getUploadedImageRefs()
    setInputValue('')
    sendMessage(
      messageText,
      pageContext,
      imageRefs.length > 0 ? imageRefs : undefined,
      wbsItemAttachments.length > 0 ? wbsItemAttachments : undefined
    )
    clearAttachments()
    dispatch(clearWbsItemAttachments())
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
  }

  const handleAttachClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      addFiles(Array.from(event.target.files))
    }
    event.target.value = ''
  }

  // スクリーンショット等をクリップボードから直接添付する。
  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    if (isStreaming) return
    const files = Array.from(event.clipboardData.files)
    if (files.length === 0) return
    // 既定の動作のままだとファイル名が入力欄に文字列として貼り付けられる。
    event.preventDefault()
    addFiles(files)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault()
      handleSendMessage()
    }
  }

  const handleRefresh = () => {
    resetConversation()
    setInputValue('')
    clearAttachments()
    dispatch(clearWbsItemAttachments())
  }

  return (
    <>
      {open && (
        <ChatPanel {...dropZoneProps} onPaste={handlePaste}>
          {isDraggingOver && (
            <DropOverlay>
              {intl.formatMessage({ id: 'aiAgentChat.attach.dropHere' })}
            </DropOverlay>
          )}
          <PanelHeader>
            <HeaderTitle>
              {intl.formatMessage({ id: 'aiAgentChat.title' })}
            </HeaderTitle>
            <HeaderActions>
              <Tooltip title={intl.formatMessage({ id: 'aiAgentChat.reset' })}>
                <span>
                  <HeaderIconButton
                    size="small"
                    onClick={handleRefresh}
                    disabled={isStreaming}
                  >
                    <RefreshIcon fontSize="small" />
                  </HeaderIconButton>
                </span>
              </Tooltip>
              <Tooltip title={intl.formatMessage({ id: 'aiAgentChat.close' })}>
                <HeaderIconButton size="small" onClick={() => setOpen(false)}>
                  <CloseIcon fontSize="small" />
                </HeaderIconButton>
              </Tooltip>
            </HeaderActions>
          </PanelHeader>
          <AiAgentMessageList messages={messages} isStreaming={isStreaming} />
          {inlineError && <InlineError>{inlineError}</InlineError>}
          {attachErrorMessage && (
            <InlineError>{attachErrorMessage}</InlineError>
          )}
          {(attachments.length > 0 || wbsItemAttachments.length > 0) && (
            <AttachmentsPreviewRow>
              {attachments.map(attachment => (
                <AttachmentPreviewItem
                  key={attachment.id}
                  hasError={attachment.status === 'error'}
                >
                  <AttachmentThumb src={attachment.previewUrl} alt="" />
                  {attachment.status === 'uploading' && (
                    <AttachmentUploadingOverlay />
                  )}
                  <RemoveAttachmentButton
                    size="small"
                    onClick={() => removeAttachment(attachment.id)}
                    aria-label={intl.formatMessage({
                      id: 'aiAgentChat.attach.remove',
                    })}
                  >
                    <CloseIcon fontSize="inherit" />
                  </RemoveAttachmentButton>
                </AttachmentPreviewItem>
              ))}
              {wbsItemAttachments.map(wbsItem => (
                <WbsItemAttachmentChip
                  key={wbsItem.code}
                  wbsItem={wbsItem}
                  onRemove={() =>
                    dispatch(removeWbsItemAttachment(wbsItem.code))
                  }
                />
              ))}
            </AttachmentsPreviewRow>
          )}
          <InputArea>
            <Tooltip
              title={intl.formatMessage({ id: 'aiAgentChat.attach.button' })}
            >
              <span>
                <HeaderIconButton
                  size="small"
                  onClick={handleAttachClick}
                  disabled={isStreaming}
                >
                  <AttachmentIcon fontSize="small" />
                </HeaderIconButton>
              </span>
            </Tooltip>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple={true}
              hidden={true}
              onChange={handleFileInputChange}
            />
            <ChatInput
              placeholder={intl.formatMessage({
                id: 'aiAgentChat.input.placeholder',
              })}
              fullWidth={true}
              multiline={true}
              maxRows={4}
              inputProps={{
                'aria-label': intl.formatMessage({
                  id: 'aiAgentChat.input.placeholder',
                }),
              }}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={isStreaming}
            />
            <Tooltip title={intl.formatMessage({ id: 'aiAgentChat.send' })}>
              <span>
                <SendButton
                  onClick={handleSendMessage}
                  disabled={
                    isStreaming || inputValue.trim() === '' || isUploading
                  }
                >
                  <SendIcon />
                </SendButton>
              </span>
            </Tooltip>
          </InputArea>
        </ChatPanel>
      )}
      {/* 引っ込めたかどうかを復元するまで描画しない。先に描画すると、
          引っ込めたはずのボタンが読み込みのたびに一瞬見えてしまう。 */}
      {isRestored && (
        <FabContainer displayState={displayState}>
          <Tooltip title={intl.formatMessage({ id: 'aiAgentChat.title' })}>
            <ChatFab
              aria-label={intl.formatMessage({ id: 'aiAgentChat.title' })}
              onClick={handleFabClick}
            >
              {open ? <CloseIcon /> : <ChatIcon />}
            </ChatFab>
          </Tooltip>
          {displayState === 'visible' && !open && (
            <Tooltip title={intl.formatMessage({ id: 'aiAgentChat.dismiss' })}>
              <DismissButton
                aria-label={intl.formatMessage({ id: 'aiAgentChat.dismiss' })}
                onClick={handleDismiss}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </DismissButton>
            </Tooltip>
          )}
        </FabContainer>
      )}
    </>
  )
}
